import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";
import type { GuestStrategyListItem, SharedStrategy } from "@/types/strategyShare.type";

/**
 * The strategies an operator sent to the signed-in Guest (a brand's client).
 * Guests have no strategy of their own; the server lists only the active ones
 * sent to them. See docs/guest-role-plan.md §5c in the backend.
 */
export function useGuestStrategies(brandId: string | null) {
  return useQuery({
    queryKey: ["guestStrategies", brandId],
    queryFn: async () => {
      const res = await axiosInstance.get<{ strategies: GuestStrategyListItem[] }>(
        `/guest-strategy/${brandId}`,
      );
      return res.data.strategies;
    },
    enabled: !!brandId,
  });
}

/** Taken back, gone back to draft, or replaced by a newer version. */
export function isGuestStrategyGone(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export function useGuestStrategy(brandId: string | null, strategyId: string | null) {
  return useQuery({
    queryKey: ["guestStrategy", brandId, strategyId],
    queryFn: async () => {
      const res = await axiosInstance.get<SharedStrategy>(`/guest-strategy/${brandId}/${strategyId}`);
      return scrubVendorTermsDeep(res.data);
    },
    enabled: !!brandId && !!strategyId,
    // Not polled: a strategy is a finished document, and the response carries
    // presigned image URLs that expire in an hour. The global refetch-on-focus
    // refreshes them; this only stops a tab switch refetching a fresh load.
    staleTime: 10 * 60 * 1000,
  });
}
